import { motion, AnimatePresence } from 'framer-motion';
import { useNotifications } from '../../contexts/NotificationContext';

/**
 * Toast Notification Component
 * Shows temporary pop-up notifications with pill-shaped design
 */

const ToastNotification = () => {
  const { toasts, removeToast } = useNotifications();

  const getIconComponent = (type, icon) => {
    // Return custom icon if provided as JSX
    if (icon && typeof icon !== 'string') {
      return icon;
    }

    // Default icons based on type
    const icons = {
      warning: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      ),
      error: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      ),
      success: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ),
      info: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      ),
    };

    // If emoji icon provided, use it
    if (typeof icon === 'string') {
      return <span className="text-lg">{icon}</span>;
    }

    return icons[type] || icons.info;
  };

  const getColorStyles = (color, type) => {
    // Use primary orange for most notifications
    const styles = {
      orange: {
        bg: 'bg-primary-50',
        border: 'border-primary-200',
        iconBg: 'bg-primary-100',
        iconColor: 'text-primary-600',
        title: 'text-primary-900',
        message: 'text-primary-700',
        button: 'bg-primary-500 hover:bg-primary-600 text-white',
      },
      red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
        title: 'text-red-900',
        message: 'text-red-700',
        button: 'bg-red-500 hover:bg-red-600 text-white',
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        iconBg: 'bg-green-100',
        iconColor: 'text-green-600',
        title: 'text-green-900',
        message: 'text-green-700',
        button: 'bg-green-500 hover:bg-green-600 text-white',
      },
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        title: 'text-blue-900',
        message: 'text-blue-700',
        button: 'bg-blue-500 hover:bg-blue-600 text-white',
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        iconBg: 'bg-purple-100',
        iconColor: 'text-purple-600',
        title: 'text-purple-900',
        message: 'text-purple-700',
        button: 'bg-purple-500 hover:bg-purple-600 text-white',
      },
    };

    // Map type to color if color not specified
    const typeToColor = {
      warning: 'orange',
      error: 'red',
      success: 'green',
      info: 'blue',
    };

    const colorKey = color || typeToColor[type] || 'orange';
    return styles[colorKey] || styles.orange;
  };

  return (
    <div className="fixed top-4 right-4 z-[9999] pointer-events-none max-w-md">
      <AnimatePresence mode="wait">
        {toasts.length > 0 && (() => {
          // Show only the most recent toast
          const toast = toasts[toasts.length - 1];
          const colorStyles = getColorStyles(toast.color, toast.type);

          return (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95, transition: { duration: 0.2 } }}
              className="pointer-events-auto"
            >
              {/* Pill-shaped notification container */}
              <div
                className={`
                  flex items-center gap-3 px-4 py-3
                  rounded-full shadow-lg border
                  ${colorStyles.bg} ${colorStyles.border}
                  min-w-[280px] max-w-[420px]
                `}
              >
                {/* Icon Circle */}
                <div
                  className={`
                    flex-shrink-0 w-9 h-9 rounded-full
                    flex items-center justify-center
                    ${colorStyles.iconBg} ${colorStyles.iconColor}
                  `}
                >
                  {getIconComponent(toast.type, toast.icon)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h4 className={`font-semibold text-sm leading-tight ${colorStyles.title}`}>
                    {toast.title}
                  </h4>
                  {toast.message && (
                    <p className={`text-xs mt-0.5 truncate ${colorStyles.message}`}>
                      {toast.message}
                    </p>
                  )}
                </div>

                {/* Action Button or Close */}
                {toast.actionUrl && toast.actionText ? (
                  <a
                    href={toast.actionUrl}
                    className={`
                      flex-shrink-0 px-4 py-1.5 rounded-full
                      text-xs font-semibold transition-colors
                      ${colorStyles.button}
                    `}
                  >
                    {toast.actionText}
                  </a>
                ) : (
                  <button
                    onClick={() => removeToast(toast.id)}
                    className={`
                      flex-shrink-0 w-7 h-7 rounded-full
                      flex items-center justify-center
                      hover:bg-black/5 transition-colors
                      ${colorStyles.iconColor} opacity-60 hover:opacity-100
                    `}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Progress Bar - subtle line under the pill */}
              {toast.duration && (
                <motion.div
                  className={`mx-6 mt-1 h-0.5 rounded-full ${colorStyles.iconBg}`}
                  initial={{ width: '100%' }}
                  animate={{ width: '0%' }}
                  transition={{ duration: toast.duration / 1000, ease: 'linear' }}
                />
              )}
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </div>
  );
};

export default ToastNotification;
